import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Check, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const STORAGE_KEY = "fw_cookie_consent";

/**
 * GDPR-compliant cookie banner.
 * Stores choice in localStorage: { status: "accepted" | "rejected", ts: ISOString }.
 * Other parts of the app (e.g. PostHog) can read window.__fwCookieConsent
 * or listen to the `fw:cookie-consent` CustomEvent to gate analytics.
 */
export const CookieBanner = () => {
    const { t } = useLanguage();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) {
                // small delay so the banner doesn't fight the hero animation
                const timer = setTimeout(() => setVisible(true), 900);
                return () => clearTimeout(timer);
            }
            window.__fwCookieConsent = JSON.parse(stored).status;
        } catch (_e) {
            setVisible(true);
        }
    }, []);

    const persist = (status) => {
        const payload = { status, ts: new Date().toISOString() };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
            window.__fwCookieConsent = status;
            window.dispatchEvent(new CustomEvent("fw:cookie-consent", { detail: payload }));
        } catch (err) {
            // localStorage unavailable (private mode / quota / disabled).
            // We still hide the banner; in dev surface the reason.
            if (process.env.NODE_ENV !== "production") {
                console.warn("[CookieBanner] persist failed:", err);
            }
        }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            data-testid="cookie-banner"
            role="dialog"
            aria-live="polite"
            aria-labelledby="cookie-banner-title"
            className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 md:px-6 md:pb-6 animate-fade-up"
        >
            <div className="mx-auto max-w-[1100px] bg-black/95 backdrop-blur-xl border border-fw/40 rounded-sm shadow-[0_-10px_40px_rgba(0,0,0,0.6)]">
                <div className="grid grid-cols-12 gap-4 md:gap-6 px-5 py-5 md:px-7 md:py-6 items-center">
                    <div className="col-span-12 md:col-span-8 lg:col-span-9">
                        <div className="flex items-center gap-3 mb-2">
                            <Cookie className="w-4 h-4 text-fw" strokeWidth={2} />
                            <span
                                id="cookie-banner-title"
                                className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw"
                            >
                                {t.cookie.overline}
                            </span>
                        </div>
                        <p className="font-display text-base md:text-lg text-white leading-snug mb-1">
                            {t.cookie.title}
                        </p>
                        <p className="text-sm text-white/65 leading-relaxed">
                            {t.cookie.body}{" "}
                            <Link
                                to="/privacy"
                                data-testid="cookie-banner-privacy-link"
                                className="text-fw hover:underline underline-offset-4"
                            >
                                {t.cookie.details}
                            </Link>
                        </p>
                    </div>

                    <div className="col-span-12 md:col-span-4 lg:col-span-3 flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={() => persist("accepted")}
                            data-testid="cookie-banner-accept"
                            className="group inline-flex items-center justify-between gap-2 px-4 py-3 bg-fw text-black font-display font-semibold text-[11px] uppercase tracking-[0.18em] rounded-sm hover:bg-fw-glow hover:glow-green transition-all duration-300"
                        >
                            <span className="flex items-center gap-2">
                                <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                                {t.cookie.accept}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => persist("rejected")}
                            data-testid="cookie-banner-reject"
                            className="group inline-flex items-center justify-between gap-2 px-4 py-3 border border-white/25 text-white font-display font-semibold text-[11px] uppercase tracking-[0.18em] rounded-sm hover:border-fw hover:text-fw transition-all duration-300"
                        >
                            <span className="flex items-center gap-2">
                                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                                {t.cookie.reject}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
